import React, { useState, useEffect } from 'react';
import { Card, Form, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import SearchForm from './components/SearchForm';
import SubscriptionTable from './components/SubscriptionTable';
import { getSubscriptionList } from './api';
import { Platform, SubscriptionSearchParams, OrderStatus } from './types';
import dayjs from 'dayjs';

const MemberSubscriptionPage: React.FC = () => {
  const isMobile = useAtomValue(isMobileAtom);
  const [form] = Form.useForm<SubscriptionSearchParams>();
  const [platform, setPlatform] = useState<Platform>(Platform.APPLE);

  useEffect(() => {
    form.setFieldsValue({ orderStatus: OrderStatus.ACTIVE });
  }, [form]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = isMobile ? 10 : 20;

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'subscription-list',
      platform,
      currentPage,
      form.getFieldsValue(),
    ],
    queryFn: async () => {
      const values = form.getFieldsValue();
      const { dateRange, ...restValues } = values;

      const params: SubscriptionSearchParams = {
        ...restValues,
        platform,
        currentPage,
        pageSize,
        // 苹果、安卓列表默认选中有效，入参默认传递
        orderStatus: restValues.orderStatus ?? OrderStatus.ACTIVE,
      };

      // 处理日期范围
      if (dateRange && dateRange.length === 2) {
        params.startDate = dayjs(dateRange[0]).format('YYYY-MM-DD');
        params.endDate = dayjs(dateRange[1]).format('YYYY-MM-DD');
      }

      return getSubscriptionList(params);
    },
  });

  const handleSearch = () => {
    setCurrentPage(1);
    refetch();
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ orderStatus: OrderStatus.ACTIVE });
    setCurrentPage(1);
    refetch();
  };

  const handlePlatformChange = (newPlatform: Platform) => {
    // 切换 platform 时清空 notificationType（安卓数字 vs 苹果字符串，类型不兼容）
    form.setFieldsValue({ notificationType: undefined });
    setPlatform(newPlatform);
    setCurrentPage(1);
    refetch();
  };

  const handleTableChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="order-page-container">
      <SearchForm
        form={form}
        platform={platform}
        onPlatformChange={handlePlatformChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      <SubscriptionTable
        data={data?.records || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data?.total || 0,
          onChange: handleTableChange,
        }}
      />
    </div>
  );
};

export default MemberSubscriptionPage;
